// Budget as per Schedule — Feb 2026 (v2).
//
// Aggregate view mirroring the Cost Sheet Editor's "Cost Break-up" +
// "Cost Distribution" pie chart, but rolled up across EVERY part that
// has both a Cost Sheet AND scheduled orders in the Sales Register.
//
// Multiplier per part: sum(ordered_qty) for the selected delivery-month
// (all months when Month = All).
//
// Break-up sections:
//   • Raw Material   — total + per-grade sub-lines (rm_grade from Cost Sheet)
//   • Process Cost   — total + per-process sub-lines (each cost key in totals)
//   • ICC / Rejection / Overhead (recomputed on aggregates)
//   • Manufacturing Cost, Revenue, Profit, Profit %
//
// The Cost Distribution pie mirrors the "Component × qty" totals so both
// visuals stay consistent, exactly like the per-part Cost Sheet page.

import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Notebook, Warning } from "@phosphor-icons/react";
import { MONTH_NAMES } from "../contexts/MonthContext";
import { API, useProcesses } from "../App";

const rupee = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// Same palette + tone helper as CostSheetEditor so the visuals feel
// like the aggregate cousin of the per-part editor.
const PIE_COLORS = ["#1D2951", "#D4A843", "#0EA5E9", "#F97316", "#8B5CF6",
                    "#10B981", "#EF4444", "#EC4899", "#14B8A6", "#F59E0B",
                    "#6366F1", "#84CC16"];

const profitTone = (pct) => {
  if (pct < 0)  return { bg: "#FEE2E2", fg: "#7F1D1D", border: "#FCA5A5", label: "High Risk" };
  if (pct < 5)  return { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5", label: "Risk" };
  if (pct < 10) return { bg: "#FFEDD5", fg: "#9A3412", border: "#FED7AA", label: "Okay" };
  if (pct < 15) return { bg: "#FEF9C3", fg: "#854D0E", border: "#FDE047", label: "Fair" };
  if (pct < 20) return { bg: "#DCFCE7", fg: "#166534", border: "#BBF7D0", label: "Good" };
  return          { bg: "#D1FAE5", fg: "#064E3B", border: "#86EFAC", label: "Healthy" };
};

// Prettify a raw process key ("machining_p1" → "Machining P1") using the
// live per-tenant Process Master when available.
function useProcessLabels() {
  const { opNames } = useProcesses();
  return (key) => {
    const clean = (key || "").replace(/__\d+$/, ""); // strip duplicate suffix
    if (opNames?.[clean]) return opNames[clean];
    return clean
      .replace(/_/g, " ")
      .replace(/\b\w/g, c => c.toUpperCase());
  };
}

// Human labels for the non-process cost keys the Cost Sheet writes into
// its `totals` map. Anything not here falls back to the process-label
// helper (Title-cased).
const KNOWN_LABEL = {
  raw_material: "Raw Material",
  cutting: "Cutting",
  cnc: "CNC Machining",
  vmc: "VMC Machining",
  machining: "Machining",
  deburring: "Deburring",
  welding: "Welding",
  surface_treatment: "Surface Treatment",
  surface_finish: "Surface Finish",
  inspection: "Inspection",
  packaging: "Packaging",
  dispatch: "Dispatch",
  silver_plating: "Silver Plating",
  anodising: "Anodising",
  nickel_plating: "Nickel Plating",
  hardening: "Hardening",
  nitriding: "Nitriding",
  zinc_plating: "Zinc Plating",
  phosphating: "Phosphating",
  painting: "Painting",
  outsource: "Outsource",
  firewall: "Firewall",
  incoming_store: "Incoming Store",
  semi_firewall: "Semi-Firewall",
};

// Cost-sheet-editor derived totals map has some non-cost keys we must ignore
const NON_COST_KEYS = new Set([
  "manufacturing_cost", "profit", "profit_pct",
]);

// Multiplier resolver used for both BudgetSchedule (ordered_qty) and
// PerformanceDispatch. Extracted so both files can share the aggregate
// engine below.
export function aggregateCostBreakup({ costSheets, qtyByPart, processLabelFor }) {
  // Buckets keyed by cost key (raw_material / cutting / …). Value is
  // total ₹ across every part × its qty multiplier.
  const buckets = new Map();
  // Raw material split by grade for the drill-down under Raw Material.
  const rmByGrade = new Map();
  let total_manufacturing = 0;
  let total_revenue = 0;
  let total_qty = 0;
  let parts_covered = 0;
  let parts_missing_qty = 0;

  costSheets.forEach(cs => {
    const qty = Number(qtyByPart.get(cs.part_id) || qtyByPart.get(`no:${cs.part_no}`) || 0);
    if (qty <= 0) { parts_missing_qty += 1; return; }
    parts_covered += 1;
    total_qty += qty;
    total_manufacturing += Number(cs.totals?.manufacturing_cost || 0) * qty;
    total_revenue       += Number(cs.selling_price || 0) * qty;

    const totals = cs.totals || {};
    Object.entries(totals).forEach(([k, v]) => {
      if (NON_COST_KEYS.has(k)) return;
      const cost = Number(v || 0) * qty;
      if (cost === 0) return;
      const cur = buckets.get(k) || { key: k, label: KNOWN_LABEL[k] || processLabelFor(k), cost: 0 };
      cur.cost += cost;
      buckets.set(k, cur);
    });

    // Per-grade RM: pull grade from lines.raw_material (fallback to Cost
    // Sheet's part-level rm_grade). rm cost/unit lives in totals.raw_material.
    const rmCostPerUnit = Number(totals.raw_material || 0);
    if (rmCostPerUnit > 0) {
      const rmLine = cs.lines?.raw_material || {};
      const grade = (rmLine.rm_grade || cs.rm_grade || "Ungraded").trim() || "Ungraded";
      const rmType = rmLine.rm_type || "";
      const key = rmType ? `${grade} · ${rmType}` : grade;
      const cur = rmByGrade.get(key) || { label: key, cost: 0, qty: 0 };
      cur.cost += rmCostPerUnit * qty;
      cur.qty  += qty;
      rmByGrade.set(key, cur);
    }
  });

  const raw_material_cost = buckets.get("raw_material")?.cost || 0;
  const process_lines = Array.from(buckets.values())
    .filter(b => b.key !== "raw_material")
    .sort((a, b) => b.cost - a.cost);
  const process_cost = process_lines.reduce((s, b) => s + b.cost, 0);

  // Overhead/ICC/rejection follow the same formulas the Cost Sheet editor
  // uses per-part — applied here on aggregate values so the roll-up ties
  // out to what individual sheets show.
  const icc       = raw_material_cost * 0.01;
  const rejection = raw_material_cost * 0.01;
  const overhead  = process_cost * 0.20;

  // Recompute total manufacturing from aggregate parts so it matches
  // exactly what the visual add-up shows (rounding drift-free).
  const manufacturing_cost_agg = raw_material_cost + process_cost + icc + rejection + overhead;
  const profit = total_revenue - manufacturing_cost_agg;
  const profit_pct = total_revenue > 0 ? (profit / total_revenue) * 100 : 0;

  const rmGradeList = Array.from(rmByGrade.values()).sort((a, b) => b.cost - a.cost);

  return {
    raw_material_cost,
    process_cost,
    process_lines,
    rm_grade_lines: rmGradeList,
    icc, rejection, overhead,
    manufacturing_cost: manufacturing_cost_agg,
    revenue: total_revenue,
    profit, profit_pct,
    total_qty,
    parts_covered, parts_missing_qty,
  };
}

export default function BudgetSchedule() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(0); // 0 = All months
  const [sales, setSales] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const processLabelFor = useProcessLabels();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, cs] = await Promise.all([
        axios.get(`${API}/sales-register`,          { skipPeriod: true }),
        axios.get(`${API}/cost-sheets?full=true`,   { skipPeriod: true }),
      ]);
      setSales(sr.data?.data || []);
      setSheets(cs.data || []);
    } catch { toast.error("Failed to load Budget data"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Sum ordered_qty per part respecting the delivery-month filter.
  const qtyByPart = useMemo(() => {
    const m = new Map();
    sales.forEach(r => {
      const dd = (r.delivery_date || "").slice(0, 7);
      const [yy, mm] = dd ? dd.split("-").map(Number) : [null, null];
      if (year && yy && yy !== year) return;
      if (month && mm && mm !== month) return;
      // Rows without a delivery date only count when no year filter is applied.
      if (year && !yy) return;
      const q = Number(r.ordered_qty || 0);
      if (!q) return;
      const key = r.part_id || `no:${r.part_no}`;
      m.set(key, (m.get(key) || 0) + q);
    });
    return m;
  }, [sales, year, month]);

  const agg = useMemo(() => aggregateCostBreakup({
    costSheets: sheets, qtyByPart, processLabelFor,
  }), [sheets, qtyByPart, processLabelFor]);

  const pieData = useMemo(() => {
    const arr = [
      { name: "Raw Material", value: agg.raw_material_cost, key: "raw_material" },
      ...agg.process_lines.map(p => ({ name: p.label, value: p.cost, key: p.key })),
    ].filter(d => d.value > 0);
    return arr;
  }, [agg]);

  const yearOpts = useMemo(() => {
    const s = new Set([new Date().getFullYear()]);
    sales.forEach(r => {
      const y = Number((r.delivery_date || "").slice(0, 4));
      if (y) s.add(y);
    });
    return Array.from(s).sort((a, b) => b - a);
  }, [sales]);

  const periodLabel = month
    ? `${MONTH_NAMES[month - 1]} ${year}`
    : `All months · ${year}`;

  return (
    <Layout title="Budget as per Schedule">
      <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951] flex-shrink-0">
              <Notebook size={22} weight="fill" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1D2951]" style={{ fontFamily: "Chivo" }}>
                Budget as per Schedule
              </h1>
              <p className="text-xs md:text-sm text-[#64748B] mt-0.5">
                Cost Break-up &amp; Distribution across all parts, weighted by scheduled order quantities.
              </p>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] block mb-1">Month</label>
              <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
                <SelectTrigger className="h-9 w-32" data-testid="budget-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">All months</SelectItem>
                  {MONTH_NAMES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] block mb-1">Year</label>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="h-9 w-24" data-testid="budget-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOpts.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Context strip */}
        <Card className="p-3 flex flex-wrap items-center gap-x-6 gap-y-2 border border-[#E2E8F0] text-xs">
          <ContextPill label="Period" value={periodLabel} />
          <ContextPill label="Total Scheduled Qty" value={num(agg.total_qty)} />
          <ContextPill label="Parts Covered" value={agg.parts_covered} />
          {agg.parts_missing_qty > 0 && (
            <ContextPill label="Cost Sheets w/o Orders" value={agg.parts_missing_qty} tone="#B45309" />
          )}
        </Card>

        {agg.total_qty === 0 && !loading && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-[#FEF3C7] border border-[#F59E0B] text-xs text-[#78350F]"
            data-testid="budget-empty-warning">
            <Warning size={16} weight="fill" className="text-[#B45309]" />
            <span>No scheduled orders match this period. Try a different month/year or ensure Sales Register + Cost Sheets are populated.</span>
          </div>
        )}

        {/* Break-up + Pie (mirrors CostSheetEditor.js summary section) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CostBreakupCard agg={agg} loading={loading}
            title="Cost Break-up (Aggregate)" testPrefix="bs" />
          <Card className="p-5 border border-[#E2E8F0]">
            <h3 className="text-sm font-bold text-[#1D2951] mb-3">Cost Distribution</h3>
            {pieData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-[#94A3B8]">
                {loading ? "Loading…" : "No cost data for the selected period."}
              </div>
            ) : (
              <div className="h-80" data-testid="bs-pie">
                <ResponsiveContainer>
                  <PieChart margin={{ top: 24, right: 24, bottom: 8, left: 24 }}>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      outerRadius={80} innerRadius={40} paddingAngle={2}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: "#94A3B8", strokeWidth: 1 }}>
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => rupee(v)} />
                    <Legend verticalAlign="bottom" height={36} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}

// ─── Shared building blocks ─────────────────────────────────────────
export function ContextPill({ label, value, tone = "#1D2951" }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-bold uppercase tracking-widest text-[#64748B]">{label}</span>
      <span className="font-mono font-bold" style={{ color: tone }}>{value}</span>
    </div>
  );
}

// The Cost Break-up card — replica of CostSheetEditor's card layout but
// driven by the aggregate object produced by aggregateCostBreakup().
//
// `rejectionOverride` (optional): when provided, replaces the default
// "1% of RM" rejection line with the real rejection loss ₹ passed in,
// and rebases Total Manufacturing Cost / Profit accordingly. Used by
// Performance-as-per-Dispatch to show actual rejection ₹ instead of the
// budgetary buffer.
export function CostBreakupCard({ agg, loading, title, testPrefix = "bs", extraFooter, rejectionOverride }) {
  const hasOverride = rejectionOverride !== undefined && rejectionOverride !== null;
  const rejectionValue = hasOverride ? Number(rejectionOverride) : agg.rejection;
  // Total Manufacturing Cost recomputed if rejection was overridden so all
  // downstream figures (Profit, Profit %) stay internally consistent.
  const manufacturing_cost = hasOverride
    ? (agg.raw_material_cost + agg.process_cost + agg.icc + rejectionValue + agg.overhead)
    : agg.manufacturing_cost;
  const profit = agg.revenue - manufacturing_cost;
  const profit_pct = agg.revenue > 0 ? (profit / agg.revenue) * 100 : 0;
  // Tone is derived from the displayed profit_pct so the Profit tile
  // stays honest when the override changes the outcome.
  const tone = profitTone(profit_pct);

  const rmShare  = manufacturing_cost > 0 ? agg.raw_material_cost / manufacturing_cost * 100 : 0;
  const pcShare  = manufacturing_cost > 0 ? agg.process_cost      / manufacturing_cost * 100 : 0;
  const showGrade = agg.rm_grade_lines.length > 0;

  return (
    <Card className="p-5 border border-[#E2E8F0]">
      <h3 className="text-sm font-bold text-[#1D2951] mb-3">{title}</h3>
      <div className="space-y-1.5">
        {/* Raw Material */}
        <div className="flex items-center justify-between text-sm py-1 border-b border-[#F1F5F9]">
          <span className="text-[#334155]">Raw Material</span>
          <span className="flex items-center gap-3">
            <span className="text-[10px] text-[#94A3B8] font-mono w-12 text-right">{rmShare.toFixed(1)}%</span>
            <span className="font-mono font-semibold text-[#0F172A] w-28 text-right" data-testid={`${testPrefix}-rm-cost`}>
              {rupee(agg.raw_material_cost)}
            </span>
          </span>
        </div>
        {/* Per-grade RM sub-lines */}
        {showGrade && agg.rm_grade_lines.map(g => {
          const share = agg.raw_material_cost > 0 ? g.cost / agg.raw_material_cost * 100 : 0;
          return (
            <div key={g.label} className="flex items-center justify-between text-[11px] py-0.5 pl-4 text-[#64748B]"
              data-testid={`${testPrefix}-rm-grade-${g.label}`}>
              <span className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-[#94A3B8]" />
                {g.label}
              </span>
              <span className="flex items-center gap-3">
                <span className="text-[10px] text-[#94A3B8] font-mono w-12 text-right">{share.toFixed(1)}%</span>
                <span className="font-mono w-28 text-right">{rupee(g.cost)}</span>
              </span>
            </div>
          );
        })}

        {/* Process Cost + per-process sub-lines */}
        <div className="flex items-center justify-between text-sm py-1 border-b border-[#F1F5F9]">
          <span className="text-[#334155]">
            Process Cost
            <span className="text-[10px] text-[#94A3B8] ml-1">(all non-RM processes)</span>
          </span>
          <span className="flex items-center gap-3">
            <span className="text-[10px] text-[#94A3B8] font-mono w-12 text-right">{pcShare.toFixed(1)}%</span>
            <span className="font-mono font-semibold text-[#0F172A] w-28 text-right" data-testid={`${testPrefix}-process-cost`}>
              {rupee(agg.process_cost)}
            </span>
          </span>
        </div>
        {agg.process_lines.map(p => {
          const share = agg.process_cost > 0 ? p.cost / agg.process_cost * 100 : 0;
          return (
            <div key={p.key} className="flex items-center justify-between text-[11px] py-0.5 pl-4 text-[#64748B]"
              data-testid={`${testPrefix}-pc-line-${p.key}`}>
              <span className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-[#94A3B8]" />
                {p.label}
              </span>
              <span className="flex items-center gap-3">
                <span className="text-[10px] text-[#94A3B8] font-mono w-12 text-right">{share.toFixed(1)}%</span>
                <span className="font-mono w-28 text-right">{rupee(p.cost)}</span>
              </span>
            </div>
          );
        })}

        <div className="flex items-center justify-between pt-2 border-t-2 border-[#1D2951]">
          <span className="font-black text-[#1D2951]">Total Manufacturing Cost</span>
          <span className="font-mono font-black text-lg text-[#1D2951]" data-testid={`${testPrefix}-mfg-cost`}>
            {rupee(manufacturing_cost)}
          </span>
        </div>

        {/* Overhead buffers */}
        <div className="flex items-center justify-between text-sm pt-2 text-[#475569]">
          <span>ICC <span className="text-[10px] text-[#94A3B8]">(1% of RM value)</span></span>
          <span className="font-mono font-semibold" data-testid={`${testPrefix}-icc`}>{rupee(agg.icc)}</span>
        </div>
        <div className="flex items-center justify-between text-sm text-[#475569]">
          <span>
            Rejection{" "}
            <span className="text-[10px] text-[#94A3B8]">
              {hasOverride ? "(actual — reject qty × cost-at-reject)" : "(1% of RM value)"}
            </span>
          </span>
          <span className="font-mono font-semibold" data-testid={`${testPrefix}-rejection`}>{rupee(rejectionValue)}</span>
        </div>
        <div className="flex items-center justify-between text-sm text-[#475569]">
          <span>Overhead <span className="text-[10px] text-[#94A3B8]">(20% of Process Cost)</span></span>
          <span className="font-mono font-semibold" data-testid={`${testPrefix}-overhead`}>{rupee(agg.overhead)}</span>
        </div>
      </div>

      {/* Revenue + Profit tile (matches CostSheetEditor's profit banner) */}
      <div className="mt-4 space-y-2 pt-4 border-t border-[#E2E8F0]">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#334155] font-semibold">Total Revenue</span>
          <span className="font-mono font-bold text-[#0F172A]" data-testid={`${testPrefix}-revenue`}>{rupee(agg.revenue)}</span>
        </div>
        <div className="flex items-center justify-between text-xs text-[#64748B]">
          <span>Total Quantity</span>
          <span className="font-mono">{num(agg.total_qty)}</span>
        </div>
      </div>

      <div className="mt-3 p-4 rounded" style={{ background: tone.bg, border: `1px solid ${tone.border}` }}>
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: tone.fg }}>Profit</span>
          <span className="text-2xl font-black font-mono" style={{ color: tone.fg }}
            data-testid={`${testPrefix}-profit-pct`}>
            {profit_pct.toFixed(2)}%
          </span>
        </div>
        <div className="mt-1 text-xl font-mono font-bold" style={{ color: tone.fg }}
          data-testid={`${testPrefix}-profit`}>
          {rupee(profit)}
        </div>
        <div className="text-[11px] mt-1" style={{ color: tone.fg }}>{tone.label}</div>
      </div>

      {extraFooter}

      {loading && (
        <div className="mt-3 text-center text-xs text-[#94A3B8]">Loading…</div>
      )}
    </Card>
  );
}
